import { useEffect, useState } from 'react'
import type { JSX } from 'react'

import {
  Combobox,
  Skeleton,
  Text,
  Button,
  Flex,
  Label,
  Dialog,
  Paragraph,
  Notice,
  Separator,
  Heading,
  Link,
} from '@buffer-mono/popcorn'

import {
  useCurrentOrganization,
  useHasOrganizationAction,
} from '~publish/contexts/AccountContext'

import settingsStyle from '../../Settings.module.css'
import { useGetValidNewOrganizationOwners } from '../hooks/useGetValidNewOrganizationOwners'
import { useTransferOrganizationOwnership } from '../hooks/useTransferOrganizationOwnership'

import styles from './TransferOwner.module.css'

const TransferOwner = (): JSX.Element | null => {
  const canTransferOwnership = useHasOrganizationAction('transferOwnership')
  const currentOrganization = useCurrentOrganization()
  const { validNewOwners, currentOwnerEmail, isLoading, hasError } =
    useGetValidNewOrganizationOwners()
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | undefined>()
  const [ownerChanged, setOwnerChanged] = useState(false)
  const [hasTransferred, setHasTransferred] = useState(false)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const {
    transferOrganizationOwnership,
    isLoading: isTransferring,
    hasError: hasTransferError,
  } = useTransferOrganizationOwnership()

  useEffect(() => {
    if (validNewOwners.length > 0) {
      const owner = validNewOwners.find(
        (candidateOwner) => candidateOwner.email === currentOwnerEmail,
      )
      setSelectedOwnerId(owner?.id)
    }
  }, [validNewOwners, currentOwnerEmail])

  useEffect(() => {
    const selectedOwnerObj = validNewOwners.find(
      (owner) => owner.id === selectedOwnerId,
    )
    setOwnerChanged(selectedOwnerObj?.email !== currentOwnerEmail)
  }, [selectedOwnerId, currentOwnerEmail, validNewOwners])

  if (!canTransferOwnership || !currentOrganization?.isOneBufferOrganization) {
    return null
  }

  if (isLoading) {
    return <Skeleton width="100%" height="40px" />
  }

  if (hasError) {
    return (
      <Text>Error loading valid new owners, please reload and try again.</Text>
    )
  }

  const selectedOwnerObj = validNewOwners.find(
    (owner) => owner.id === selectedOwnerId,
  )

  const disableActionButtons = isTransferring || hasTransferred

  // This is to prevent the dialog from being closed if the action buttons are disabled
  const onDialogOpenChange = (open: boolean): void => {
    if (!open && disableActionButtons) {
      return
    }
    setIsDialogOpen(open)
  }

  return (
    <>
      <Flex direction="column" gap="xs" fullWidth>
        <Separator />
        <Heading as="h2" size="small">
          Account Ownership
        </Heading>
        <Paragraph color="subtle">
          Change ownership of this Buffer account.
        </Paragraph>
        <Notice variant="info">
          <Paragraph>
            The new owner needs to be an Admin on your{' '}
            <Link
              href="https://publish.buffer.com/settings/team#/team-members"
              external
              style={{ textDecoration: 'underline', fontWeight: 'bold' }}
            >
              Buffer Organization.
            </Link>{' '}
            All eligible team members are shown in the drop-down menu below.
            Don&apos;t see a team member you&apos;d expect to see? Visit the{' '}
            <Link
              href="https://support.buffer.com/articles/changing-the-owner-of-your-buffer-account-aCU4tRKZGW"
              style={{ textDecoration: 'underline' }}
              external
            >
              Help Center
            </Link>{' '}
            for more information.
          </Paragraph>
        </Notice>
      </Flex>
      <Flex gap="sm" direction="row" align="end" wrap="wrap" fullWidth>
        <div className={styles.ownerField}>
          <Label htmlFor="account-owner">Account Owner</Label>
          <Combobox
            value={selectedOwnerId}
            onChange={(value): void => {
              setSelectedOwnerId(value)
            }}
            multiple={false}
          >
            <Combobox.Trigger
              id="account-owner"
              style={{ marginTop: 'var(--space-2xs)' }}
            >
              <Button variant="secondary" className={settingsStyle.fullWidth}>
                {selectedOwnerObj?.email}{' '}
                {selectedOwnerObj?.email === currentOwnerEmail &&
                  ' • current owner'}
              </Button>
            </Combobox.Trigger>
            <Combobox.Content className={styles.triggerWidthContent}>
              <Combobox.Input placeholder="Search team members" />
              <Combobox.List>
                <Combobox.Empty>No accounts found</Combobox.Empty>
                {validNewOwners.map((teamMember) => (
                  <Combobox.Item key={teamMember.id} value={teamMember.id}>
                    {teamMember.email}{' '}
                    {teamMember.email === currentOwnerEmail &&
                      ' • current owner'}
                  </Combobox.Item>
                ))}
              </Combobox.List>
            </Combobox.Content>
          </Combobox>
        </div>
        {/* ---------------------------------- */}
        {/* Dialog to confirm ownership change */}
        {/* ---------------------------------- */}
        <Dialog open={isDialogOpen} onOpenChange={onDialogOpenChange}>
          <Dialog.Trigger>
            <Button
              size="medium"
              type="submit"
              variant="secondary"
              className={settingsStyle.fixedWidthButton}
              disabled={!ownerChanged}
            >
              Change Owner
            </Button>
          </Dialog.Trigger>
          <Dialog.Content>
            <Dialog.Header>
              <Dialog.Title>Confirm Account Ownership Change</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <Paragraph>
                {isTransferring &&
                  !hasTransferred &&
                  'Transferring ownership... please wait.'}
                {hasTransferred && (
                  <>
                    Account ownership transferred successfully. This page will
                    reload in 5 seconds.
                  </>
                )}
                {!hasTransferred && (
                  <>
                    You&apos;re about to make <b>{selectedOwnerObj?.email}</b>{' '}
                    the new account owner.
                  </>
                )}
              </Paragraph>
              {hasTransferError && (
                <Notice variant="error">
                  Error transferring ownership, please try again.
                </Notice>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.Close>
                <Button variant="tertiary" disabled={disableActionButtons}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                variant="critical"
                onClick={(): void => {
                  void (async (): Promise<void> => {
                    const result = await transferOrganizationOwnership({
                      organizationId: currentOrganization?.id ?? '',
                      newOwner: selectedOwnerObj?.id ?? '',
                    })
                    if (result.success) {
                      setHasTransferred(true)
                      setTimeout((): void => {
                        window.location.reload()
                      }, 5000)
                    }
                  })()
                }}
                disabled={disableActionButtons}
              >
                {isTransferring ? 'Please wait...' : 'Confirm Ownership Change'}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog>
      </Flex>
    </>
  )
}

export default TransferOwner
